// src/utils/odfScore.js
// ODF Score — 6-dimension weighted performance grading system
// Each dimension scored 0–100, then weighted to produce final score

/**
 * DIMENSIONS & WEIGHTS
 * Profitability  25% — ROI, profit factor, expectancy
 * Consistency    20% — profitable weeks/months ratio
 * Risk Control   20% — drawdown, loss sizing, daily limit discipline
 * Execution      15% — actual RR vs planned, win/loss quality
 * Discipline     15% — rule adherence, mistake patterns
 * Sample Size     5% — confidence weight based on trade count
 */

const WEIGHTS = {
  profitability: 0.25,
  consistency:   0.20,
  riskControl:   0.20,
  execution:     0.15,
  discipline:    0.15,
  sampleSize:    0.05,
};

export function calcODFScore(trades, settings) {
  const closed = (trades || []).filter(t => t.status !== 'open');
  const startBal = settings?.startingBalance || 0;

  if (closed.length < 5) return null; // need at least 5 trades

  const dims = {
    profitability: scoreProfitability(closed, startBal),
    consistency:   scoreConsistency(closed),
    riskControl:   scoreRiskControl(closed, startBal),
    execution:     scoreExecution(closed),
    discipline:    scoreDiscipline(closed),
    sampleSize:    scoreSampleSize(closed.length),
  };

  // Weighted total
  const total = Math.round(
    Object.entries(WEIGHTS).reduce((sum, [key, weight]) => sum + (dims[key].score * weight), 0)
  );

  // Grade
  const { grade, color, label, desc } = getGrade(total);

  // Anomaly detection
  const anomalies = detectAnomalies(closed);

  // Credibility tier
  const credibility = getCredibilityTier(closed.length, anomalies, dims);

  // Improvement tips
  const tips = buildTips(dims, anomalies);

  return {
    total,
    grade,
    color,
    label,
    desc,
    dims,
    anomalies,
    credibility,
    tips,
    tradeCount: closed.length,
  };
}

// ── DIMENSION 1: PROFITABILITY ────────────────────────────────────
function scoreProfitability(trades, startBal) {
  const wins   = trades.filter(t => t.result === 'Win');
  const losses = trades.filter(t => t.result === 'Loss');
  const total  = trades.length;

  const grossProfit = wins.reduce((s, t) => s + Number(t.pnl || 0), 0);
  const grossLoss   = Math.abs(losses.reduce((s, t) => s + Number(t.pnl || 0), 0));
  const totalPnl    = trades.reduce((s, t) => s + Number(t.pnl || 0), 0);

  // Profit factor: higher is better (1.5+ is good, 2.0+ is strong)
  const pf = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? 5 : 0;
  const pfScore = Math.min(100, (pf / 2.5) * 100);

  // Expectancy per trade: avg P&L per trade
  const expectancy = total > 0 ? totalPnl / total : 0;
  const expScore = expectancy > 0 ? Math.min(100, (expectancy / 50) * 100) : 0;

  // ROI (if starting balance known)
  let roiScore = 50; // neutral if no balance
  if (startBal > 0) {
    const roi = (totalPnl / startBal) * 100;
    roiScore = roi > 0 ? Math.min(100, (roi / 20) * 100) : Math.max(0, 50 + roi * 2);
  }

  const score = Math.round((pfScore * 0.45) + (expScore * 0.25) + (roiScore * 0.30));

  return {
    score: Math.min(100, Math.max(0, score)),
    label: 'Profitability',
    detail: `PF: ${pf.toFixed(2)} · Expectancy: ${expectancy >= 0 ? '+' : ''}$${expectancy.toFixed(0)}/trade`,
    tip: pf < 1.2 ? 'Your profit factor is below 1.2. Focus on cutting losses shorter or letting winners run longer.' :
         pf < 1.5 ? 'Good profit factor. Aim for 1.5+ by improving your exit discipline.' : null,
  };
}

// ── DIMENSION 2: CONSISTENCY ─────────────────────────────────────
function scoreConsistency(trades) {
  if (trades.length < 10) return { score: 50, label: 'Consistency', detail: 'Need 10+ trades', tip: null };

  // Group by week
  const byWeek = {};
  for (const t of trades) {
    if (!t.date) continue;
    const d   = new Date(t.date);
    const mon = new Date(d); mon.setDate(d.getDate() - d.getDay() + 1);
    const key = mon.toISOString().slice(0, 10);
    if (!byWeek[key]) byWeek[key] = 0;
    byWeek[key] += Number(t.pnl || 0);
  }

  const weeks       = Object.values(byWeek);
  const totalWeeks  = weeks.length;
  if (totalWeeks < 2) return { score: 50, label: 'Consistency', detail: 'Need 2+ weeks of data', tip: null };

  const profitableWeeks = weeks.filter(w => w > 0).length;
  const weeklyWinRate   = profitableWeeks / totalWeeks;

  // Variance — lower variance = more consistent
  const avg = weeks.reduce((a, b) => a + b, 0) / totalWeeks;
  const variance = weeks.reduce((s, w) => s + Math.pow(w - avg, 2), 0) / totalWeeks;
  const stdDev   = Math.sqrt(variance);
  const cv       = avg !== 0 ? Math.abs(stdDev / avg) : 1; // coefficient of variation

  // Score: high weekly win rate + low variance = high consistency
  const wrScore  = weeklyWinRate * 70;
  const varScore = Math.max(0, 30 - (cv * 10));
  const score    = Math.round(wrScore + varScore);

  return {
    score: Math.min(100, Math.max(0, score)),
    label: 'Consistency',
    detail: `${profitableWeeks}/${totalWeeks} profitable weeks (${Math.round(weeklyWinRate * 100)}%)`,
    tip: weeklyWinRate < 0.5 ? 'Less than half your weeks are profitable. Focus on capital preservation in losing weeks.' :
         weeklyWinRate < 0.7 ? 'Good weekly consistency. Target 70%+ profitable weeks for elite status.' : null,
  };
}

// ── DIMENSION 3: RISK CONTROL ────────────────────────────────────
function scoreRiskControl(trades, startBal) {
  const losses  = trades.filter(t => t.result === 'Loss');
  const total   = trades.length;
  if (total === 0) return { score: 50, label: 'Risk Control', detail: 'No trades yet', tip: null };

  // Max drawdown calculation
  let peak = startBal || 0, maxDd = 0, running = startBal || 0;
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  for (const t of sorted) {
    running += Number(t.pnl || 0);
    if (running > peak) peak = running;
    const dd = peak > 0 ? ((peak - running) / peak) * 100 : 0;
    if (dd > maxDd) maxDd = dd;
  }

  // Drawdown score: <5% = 100pts, <10% = 80pts, <20% = 50pts, >30% = 0pts
  const ddScore = maxDd < 5  ? 100 :
                  maxDd < 10 ? 80  :
                  maxDd < 15 ? 60  :
                  maxDd < 20 ? 40  :
                  maxDd < 30 ? 20  : 0;

  // Loss sizing consistency — are losses similar in size?
  const lossPnls = losses.map(t => Math.abs(Number(t.pnl || 0))).filter(v => v > 0);
  let sizeConsistency = 100;
  if (lossPnls.length >= 3) {
    const avgLoss = lossPnls.reduce((a, b) => a + b, 0) / lossPnls.length;
    const maxLoss = Math.max(...lossPnls);
    // If max loss is more than 3x average loss = poor risk control
    sizeConsistency = maxLoss > avgLoss * 3 ? 40 : maxLoss > avgLoss * 2 ? 70 : 100;
  }

  const score = Math.round((ddScore * 0.6) + (sizeConsistency * 0.4));

  return {
    score: Math.min(100, Math.max(0, score)),
    label: 'Risk Control',
    detail: `Max DD: ${maxDd.toFixed(1)}% · Loss consistency: ${sizeConsistency === 100 ? 'Good' : sizeConsistency === 70 ? 'Fair' : 'Poor'}`,
    tip: maxDd > 20 ? `Your max drawdown of ${maxDd.toFixed(1)}% is high. Consider reducing position size after 2+ consecutive losses.` :
         maxDd > 10 ? `Drawdown at ${maxDd.toFixed(1)}%. Target under 10% for strong risk control.` :
         sizeConsistency < 70 ? 'Some losses are much larger than your average. Review your stop loss discipline.' : null,
  };
}

// ── DIMENSION 4: EXECUTION ───────────────────────────────────────
function scoreExecution(trades) {
  const withRR = trades.filter(t => t.rrAchieved && Number(t.rrAchieved) > 0);
  if (withRR.length < 5) return { score: 60, label: 'Execution', detail: 'Log RR achieved for accurate score', tip: 'Start logging your achieved RR on each trade for execution analysis.' };

  const avgRR   = withRR.reduce((s, t) => s + Number(t.rrAchieved), 0) / withRR.length;
  const wins    = trades.filter(t => t.result === 'Win');
  const winRate = trades.length > 0 ? wins.length / trades.length : 0;

  // Expected value per trade = (winRate × avgWinRR) - (lossRate × 1)
  const winRRs  = wins.filter(t => t.rrAchieved).map(t => Number(t.rrAchieved));
  const avgWinRR = winRRs.length > 0 ? winRRs.reduce((a, b) => a + b, 0) / winRRs.length : 0;
  const ev = (winRate * avgWinRR) - ((1 - winRate) * 1);

  // RR score: 1R = 40pts, 2R = 70pts, 3R+ = 100pts
  const rrScore = Math.min(100, (avgRR / 3) * 100);
  // EV score: positive EV = good
  const evScore = ev > 0 ? Math.min(100, (ev / 1.5) * 100) : Math.max(0, 50 + ev * 30);

  const score = Math.round((rrScore * 0.55) + (evScore * 0.45));

  return {
    score: Math.min(100, Math.max(0, score)),
    label: 'Execution',
    detail: `Avg RR: ${avgRR.toFixed(2)}R · EV: ${ev >= 0 ? '+' : ''}${ev.toFixed(2)}R/trade`,
    tip: avgRR < 1 ? 'Average RR under 1. You need to win more than you lose just to break even. Target 1.5R+ per trade.' :
         avgRR < 1.5 ? 'RR is positive but target 1.5R+ for sustainable profitability.' : null,
  };
}

// ── DIMENSION 5: DISCIPLINE ──────────────────────────────────────
function scoreDiscipline(trades) {
  if (trades.length === 0) return { score: 50, label: 'Discipline', detail: 'No trades', tip: null };

  const followed = trades.filter(t => t.followedRules === true || t.followedRules === 'true').length;
  const ruleRate = followed / trades.length;

  // Mistake analysis
  const mistakes = trades.filter(t => t.mistakeMade && t.mistakeMade !== 'None' && t.mistakeMade !== '');
  const mistakeRate = mistakes.length / trades.length;

  // Revenge trading detection — loss followed immediately by another loss on same day
  const sortedByDate = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  let revengeCount = 0;
  for (let i = 1; i < sortedByDate.length; i++) {
    const prev = sortedByDate[i - 1];
    const curr = sortedByDate[i];
    if (prev.result === 'Loss' && curr.result === 'Loss' &&
        prev.date === curr.date &&
        (curr.mistakeMade?.toLowerCase().includes('revenge') || curr.mistakeMade?.toLowerCase().includes('fomo'))) {
      revengeCount++;
    }
  }
  const revengeScore = Math.max(0, 100 - (revengeCount * 15));

  const ruleScore    = ruleRate * 100;
  const mistakeScore = Math.max(0, 100 - (mistakeRate * 80));

  const score = Math.round((ruleScore * 0.50) + (mistakeScore * 0.30) + (revengeScore * 0.20));

  const topMistake = getMostCommonMistake(mistakes);

  return {
    score: Math.min(100, Math.max(0, score)),
    label: 'Discipline',
    detail: `Rules followed: ${Math.round(ruleRate * 100)}% · Mistakes: ${Math.round(mistakeRate * 100)}%${topMistake ? ` · Most common: ${topMistake}` : ''}`,
    tip: ruleRate < 0.7 ? `Only ${Math.round(ruleRate * 100)}% rule adherence. Your data shows this directly hurts your results.` :
         revengeCount > 2 ? `${revengeCount} possible revenge trade patterns detected. Consider a cooling-off rule after losses.` :
         topMistake ? `Your most repeated mistake is "${topMistake}". Focus on eliminating this one pattern first.` : null,
  };
}

// ── DIMENSION 6: SAMPLE SIZE ─────────────────────────────────────
function scoreSampleSize(count) {
  const score = count < 10  ? 20 :
                count < 20  ? 40 :
                count < 50  ? 60 :
                count < 100 ? 80 : 100;
  return {
    score,
    label: 'Sample Size',
    detail: `${count} trades logged`,
    tip: count < 50 ? `${50 - count} more trades needed for high-confidence score.` : null,
  };
}

// ── ANOMALY DETECTION ────────────────────────────────────────────
export function detectAnomalies(trades) {
  if (trades.length < 10) return [];
  const flags = [];
  const wins  = trades.filter(t => t.result === 'Win');
  const wr    = wins.length / trades.length;

  // Flag 1: Suspiciously high win rate with sufficient sample
  if (trades.length >= 30 && wr > 0.88) {
    flags.push({ type: 'warning', msg: `Win rate of ${Math.round(wr * 100)}% across ${trades.length} trades is statistically rare for discretionary trading. Please verify your data is accurate.` });
  }

  // Flag 2: No losing streaks in large sample
  const sortedTrades = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  let maxConsecLosses = 0, curLosses = 0;
  for (const t of sortedTrades) {
    if (t.result === 'Loss') { curLosses++; maxConsecLosses = Math.max(maxConsecLosses, curLosses); }
    else curLosses = 0;
  }
  if (trades.length >= 40 && maxConsecLosses <= 1) {
    flags.push({ type: 'warning', msg: 'No consecutive losses detected across a large sample. Even high-performing traders experience losing streaks.' });
  }

  // Flag 3: Identical DOLLAR P&L amounts (not ratio — fixed RR is fine, fixed dollar = suspicious)
  // Only flag if the exact dollar value repeats more than 80% of the time
  const pnlDollars = trades.map(t => Number(t.pnl || 0).toFixed(2));
  const pnlCounts = {};
  for (const p of pnlDollars) pnlCounts[p] = (pnlCounts[p] || 0) + 1;
  const mostCommonPnl = Math.max(...Object.values(pnlCounts));
  if (trades.length >= 15 && mostCommonPnl / trades.length > 0.7) {
    flags.push({ type: 'warning', msg: `${Math.round((mostCommonPnl/trades.length)*100)}% of trades show the exact same P&L dollar amount. Real position sizing produces varying dollar results even with fixed RR.` });
  }

  // Flag 4: Screenshot/chart links — info note only, never a penalty
  // Checks for TradingView links specifically as stronger evidence
  const withLinks     = trades.filter(t => t.screenshotLink && t.screenshotLink.length > 8);
  const withTVLinks   = withLinks.filter(t => t.screenshotLink?.includes('tradingview') || t.screenshotLink?.includes('tv.co'));
  if (trades.length >= 20 && withLinks.length === 0) {
    flags.push({ type: 'info', msg: 'No chart links attached to any trades. Linking your TradingView charts adds evidence to your trade data analytics.' });
  } else if (trades.length >= 20 && withTVLinks.length > 0) {
    // Positive signal — TradingView chart links detected
    flags.push({ type: 'positive', msg: `${withTVLinks.length} TradingView chart link${withTVLinks.length > 1 ? 's' : ''} attached. Chart evidence strengthens your trade data analytics credibility.` });
  }

  return flags;
}

// ── CREDIBILITY TIER ─────────────────────────────────────────────
export function getCredibilityTier(tradeCount, anomalies, dims) {
  const warnings  = (anomalies || []).filter(a => a.type === 'warning').length;
  const positives = (anomalies || []).filter(a => a.type === 'positive').length;

  if (warnings >= 2) return {
    tier: 'flagged', label: 'Flagged', color: '#ff4d6d', icon: '',
    desc: 'Statistical patterns detected. Review your trade data for accuracy.',
  };

  if (tradeCount < 20)  return {
    tier: 'unverified', label: 'Unverified', color: '#55556a', icon: '○',
    desc: 'Insufficient data — log 20+ trades to begin establishing credibility.',
  };
  if (tradeCount < 50)  return {
    tier: 'early', label: 'Early Stage', color: '#8888a8', icon: '◐',
    desc: 'Building history. Early signals only — more trades needed for confidence.',
  };
  if (tradeCount < 100) return {
    tier: 'developing', label: 'Developing', color: '#ff9f43', icon: '◕',
    desc: 'Statistically meaningful start. Pattern is beginning to emerge.',
  };
  if (tradeCount < 200) return {
    tier: 'established', label: 'Established', color: '#4cc9f0', icon: '●',
    desc: 'Good trade data analytics depth. Results carry real statistical weight.',
  };
  if (tradeCount < 500) return {
    tier: 'verified', label: 'Verified', color: '#00c98a', icon: '✓',
    desc: '200+ trades logged. High-confidence performance data. Strong trade data analytics.',
  };
  return {
    tier: 'elite', label: 'Elite Trade Data Analytics', color: '#00e5a0', icon: '●',
    desc: '500+ trades logged. Exceptional consistency. Top tier trader data.',
  };
}

// ── GRADE SYSTEM ─────────────────────────────────────────────────
function getGrade(score) {
  if (score >= 85) return { grade: 'A+', color: '#00e5a0', label: 'Elite',        desc: 'Exceptional across all dimensions. Top tier trader.' };
  if (score >= 75) return { grade: 'A',  color: '#00c98a', label: 'Strong',       desc: 'Strong performance with minor areas to refine.' };
  if (score >= 62) return { grade: 'B+', color: '#7bed9f', label: 'Above Average',desc: 'Above average. Consistent improvements visible.' };
  if (score >= 50) return { grade: 'B',  color: '#ffd166', label: 'Developing',   desc: 'Solid foundation. Key areas need attention.' };
  if (score >= 38) return { grade: 'C',  color: '#ff9f43', label: 'Needs Work',   desc: 'Several weaknesses affecting performance.' };
  return                  { grade: 'D',  color: '#ff4d6d', label: 'Early Stage',  desc: 'Focus on consistency and risk management first.' };
}

// ── HELPERS ───────────────────────────────────────────────────────
function getMostCommonMistake(mistakes) {
  if (!mistakes.length) return null;
  const counts = {};
  for (const t of mistakes) { counts[t.mistakeMade] = (counts[t.mistakeMade] || 0) + 1; }
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
}

function buildTips(dims, anomalies) {
  const tips = [];
  // Add lowest-scoring dimension tip first
  const sorted = Object.entries(dims)
    .filter(([k]) => k !== 'sampleSize')
    .sort((a, b) => a[1].score - b[1].score);

  for (const [, dim] of sorted.slice(0, 3)) {
    if (dim.tip) tips.push({ type: 'improvement', msg: dim.tip, dim: dim.label });
  }
  for (const a of anomalies) {
    tips.push({ type: a.type, msg: a.msg });
  }
  return tips.slice(0, 4);
}
